import React, { useContext, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { TokenContext } from '../../../../App';
import { DateTime } from 'luxon';

const OverrideRiskLevel = ({ formData, setFormData, handleInputChange, handleFileChange, readOnly, show = false }) => {
  const [encodedAccessToken] = useContext(TokenContext);
  const decodedToken = jwtDecode(encodedAccessToken);

  const handleFileRemove = async fileName => {
    setFormData(prevState => ({
      ...prevState,
      [fileName]: '',
    }));
  };

  const handleOverRideInputChange = (e, type) => {
    let overRideRiskLevelName = '';
    let overRideRiskLevelDate = '';
    const isSelected = type === 'true' || e.target.value === 'true' || e.target.value === true;
    if (isSelected) {
      overRideRiskLevelName = decodedToken.name;
      overRideRiskLevelDate = DateTime.now().toFormat('dd-MM-yyyy, HH:mm:ss');
    }

    // 统一处理值：将布尔值或字符串转换为字符串 'true' 或 'false'
    const value = e.target.value === true || e.target.value === 'true' ? 'true' : 'false';

    setFormData(prevState => ({
      ...prevState,
      [e.target.name]: value,
      overRideRiskLevelName: overRideRiskLevelName,
      overRideRiskLevelDate: overRideRiskLevelDate,
    }));
  };

  // 当选择覆盖风险级别时，自动设置默认值（不能和riskLevel相同）
  useEffect(() => {
    const isOverrideSelected = formData.overrideRiskSelected === true || formData.overrideRiskSelected === 'true';
    if (!isOverrideSelected || !formData.riskLevel) {
      return;
    }

    const riskLevels = ['Low', 'Medium', 'High'];
    const defaultRiskLevel = riskLevels.find(level => level !== formData.riskLevel);

    // 如果当前没有选择，或者选择的值和riskLevel相同，则设置默认值
    setFormData(prevState => {
      // 如果已经有正确的值，就不更新
      if (prevState.overrideRiskLevel && prevState.overrideRiskLevel !== prevState.riskLevel) {
        return prevState;
      }

      // 如果需要设置默认值，且默认值有效
      if (defaultRiskLevel && defaultRiskLevel !== prevState.overrideRiskLevel) {
        return {
          ...prevState,
          overrideRiskLevel: defaultRiskLevel,
        };
      }

      return prevState;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.overrideRiskSelected, formData.riskLevel]);

  if (!show) {
    return null;
  }

  return (
    <div className='my-3 border-2 p-1'>
      ⚠️ Do you wish to override the system-assigned risk rating? (This action will be logged)
      <label>
        <input
          className='p-1 mx-2'
          type='radio'
          name='overrideRiskSelected'
          value={true}
          checked={formData?.overrideRiskSelected === true || formData?.overrideRiskSelected === 'true'}
          onChange={e => handleOverRideInputChange(e, 'true')}
          disabled={readOnly}
        />
        Yes
      </label>
      <label>
        <input
          className='p-1 mx-2'
          type='radio'
          name='overrideRiskSelected'
          value={false}
          checked={formData?.overrideRiskSelected === false || formData?.overrideRiskSelected === 'false'}
          onChange={e => handleOverRideInputChange(e, 'false')}
          disabled={readOnly}
        />
        No
      </label>
      {(formData.overrideRiskSelected === true || formData.overrideRiskSelected === 'true') && (
        <div className='my-1'>
          <span className='my-4'> The risk level has been manually set to: </span>
          <select
            disabled={readOnly}
            value={formData.overrideRiskLevel}
            name='overrideRiskLevel'
            onChange={handleInputChange}
            className='px-1.5 py-1 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none focus:border-green-500'
          >
            <option value=''> - Select -</option>
            <option value='Low' disabled={formData.riskLevel === 'Low'}>
              Low
            </option>
            <option value='Medium' disabled={formData.riskLevel === 'Medium'}>
              Medium
            </option>
            <option value='High' disabled={formData.riskLevel === 'High'}>
              High
            </option>
          </select>
          <br />
          <textarea
            maxLength={800}
            disabled={readOnly}
            rows={2}
            value={formData?.explainManualRisk}
            placeholder={`Explain why manual risk chosen`}
            name={'explainManualRisk'}
            onChange={handleInputChange}
            className='border p-1.5 my-2 font-normal h-11/12 w-11/12'
          ></textarea>
          <p>
            {' '}
            Overriden by: {formData.overRideRiskLevelName} on {formData?.overRideRiskLevelDate && 'on'}{' '}
            {formData.overRideRiskLevelDate}{' '}
          </p>

          <span className='my-2 font-semibold'> Override Supporting Documents: </span>
          <label className='cursor-pointer bg-gray-200 px-1 py-0.5 mt-1 rounded mx-3'>
            Browse
            <input
              maxLength={800}
              type='file'
              name='overrideSupportingDocumentURL'
              onChange={handleFileChange}
              className='pt-3 hidden'
            />
          </label>
          {formData.overrideSupportingDocumentURL && (
            <>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/get-upload?fileName=${formData.overrideSupportingDocumentURL.split('/').slice(-1)[0]}`}
                target='_blank'
                rel='noopener noreferrer'
                className='text-blue-600'
              >
                {decodeURIComponent(formData.overrideSupportingDocumentURL.split('/').slice(-1)[0])}
              </a>
              <span className='cursor-pointer' onClick={() => handleFileRemove('overrideSupportingDocumentURL')}>
                {' '}
                ❌{' '}
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default OverrideRiskLevel;
